import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  SquareStack,
  Paintbrush,
  Layers,
  PaintRoller,
  Square,
  Wind,
  Zap,
  Layout,
  Wrench,
  ClipboardCheck,
  Trash2,
  Sofa,
} from "lucide-react";

// Import images
import FCFO3 from "../assets/images/FCFO3.webp";
import FCFO4 from "../assets/images/FCFO4.webp";
import OfficeFurniture1 from "../assets/images/OfficeFurniture1.webp";
import officePartitioning1 from "../assets/images/OfficePartitioning1.webp";
import officePartitioning2 from "../assets/images/OfficePartitioning2.webp";
import officePartitioning3 from "../assets/images/OfficePartitioning3.webp";
import officePartitioning4 from "../assets/images/OfficePartitioning4.webp";
import plastering1 from "../assets/images/plastering1.webp";
import plastering3 from "../assets/images/plastering3.webp";
import plastering4 from "../assets/images/plastering4.webp";
import ceilings1 from "../assets/images/ceilings1.webp";
import ceilings2 from "../assets/images/ceilings2.webp";
import ceilings3 from "../assets/images/ceilings3.webp";
import ceilings4 from "../assets/images/ceilings4.webp";
import decorating1 from "../assets/images/decorating1.webp";
import decorating2 from "../assets/images/decorating2.webp";
import decorating3 from "../assets/images/decorating3.webp";
import decorating4 from "../assets/images/decorating4.webp";
import flooring1 from "../assets/images/flooring1.webp";
import flooring2 from "../assets/images/flooring2.webp";
import flooring3 from "../assets/images/flooring3.webp";
import flooring4 from "../assets/images/flooring4.webp";
import demolition1 from "../assets/images/demolition1.webp";
import demolition2 from "../assets/images/demolition2.webp";
import demolition3 from "../assets/images/demolition3.webp";
import demolition4 from "../assets/images/demolition4.webp";
import airconVentilation from "../assets/images/airconVentilation.webp";
import airconVentilation2 from "../assets/images/airconVentilation2.webp";
import airconVentilation3 from "../assets/images/airconVentilation3.webp";
import airconVentilation4 from "../assets/images/airconVentilation4.webp";
import elecData1 from "../assets/images/elecData1.webp";
import elecData2 from "../assets/images/elecData2.webp";
import elecData3 from "../assets/images/elecData3.webp";
import elecData4 from "../assets/images/elecData4.webp";
import spacePlanning1 from "../assets/images/spacePlanning1.webp";
import spacePlanning2 from "../assets/images/spacePlanning2.webp";
import spacePlanning3 from "../assets/images/spacePlanning3.webp";
import spacePlanning4 from "../assets/images/spacePlanning4.webp";
import OfficeFurniture2 from "../assets/images/OfficeFurniture2.webp";
import OfficeFurniture3 from "../assets/images/OfficeFurniture3.webp";
import OfficeFurniture4 from "../assets/images/OfficeFurniture4.webp";
import facilitiesMan1 from "../assets/images/facilitiesMan1.webp";
import facilitiesMan2 from "../assets/images/facilitiesMan2.webp";
import facilitiesMan3 from "../assets/images/facilitiesMan3.webp";
import facilitiesMan4 from "../assets/images/facilitiesMan4.webp";
import projMan1 from "../assets/images/projMan1.webp";
import projMan2 from "../assets/images/projMan2.webp";
import projMan3 from "../assets/images/projMan3.webp";
import projMan4 from "../assets/images/projMan4.webp";

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);

  const services = [
    {
      id: 0,
      title: "Full Commercial Fit-Out",
      icon: Building2,
      description:
        "Complete turnkey solutions including design, space planning and project delivery. We create bespoke workspaces that are modern, functional, compliant and future-proof.",
      details: [
        "Complete design and planning services",
        "Full project delivery and management",
        "Bespoke workspace solutions",
        "Modern, functional and compliant designs",
        "Future-proof commercial spaces",
        "Turnkey solutions from start to finish",
      ],
      features: [
        { icon: ClipboardCheck, text: "Full project management" },
        { icon: Layout, text: "Space planning" },
        { icon: Building2, text: "Turnkey solutions" },
        { icon: Wrench, text: "Complete delivery" },
      ],
    },
    {
      id: 1,
      title: "Office Partitioning",
      icon: SquareStack,
      description:
        "We specialise in all types of office and warehouse partitioning systems. They create privacy, reduce noise and define your space.",
      details: [
        "'Soundproof' partitions",
        "Glazed screens",
        "Frameless glass dividers",
        "Modular office dividers",
        "Ideal for executive offices, meeting rooms, quiet zones and collaborative spaces",
        "Custom solutions for any workspace",
      ],
      features: [
        { icon: SquareStack, text: "Multiple partition types" },
        { icon: Wind, text: "Soundproof options" },
        { icon: Layout, text: "Space definition" },
        { icon: Building2, text: "Privacy solutions" },
      ],
    },
    {
      id: 2,
      title: "Dry-Lining & Plastering",
      icon: Paintbrush,
      description:
        "We specialise in all aspects of dry-lining, including taping & jointing, skimming & plastering, and fire protection upgrades. Delivering smooth, durable surfaces ready for decoration.",
      details: [
        "Taping & jointing",
        "Skimming & plastering",
        "Fire protection upgrades",
        "Smooth, durable surfaces",
        "Ready for decoration",
        "Professional finish guaranteed",
      ],
      features: [
        { icon: Paintbrush, text: "Professional finish" },
        { icon: Wrench, text: "Fire protection" },
        { icon: ClipboardCheck, text: "Quality surfaces" },
        { icon: Building2, text: "Ready for decoration" },
      ],
    },
    {
      id: 3,
      title: "Ceilings (Suspended & MF)",
      icon: Layers,
      description:
        "We are specialist installers of all types of suspended ceilings and MF plasterboard ceilings to improve acoustics, aesthetics, accessibility and fire compliance.",
      details: [
        "Suspended ceiling installation",
        "MF plasterboard ceilings",
        "Improved acoustics",
        "Enhanced aesthetics",
        "Better accessibility",
        "Fire compliance solutions",
      ],
      features: [
        { icon: Layers, text: "Suspended systems" },
        { icon: Wind, text: "Acoustic solutions" },
        { icon: ClipboardCheck, text: "Fire compliance" },
        { icon: Building2, text: "Professional installation" },
      ],
    },
    {
      id: 4,
      title: "Interior & Exterior Decorating",
      icon: PaintRoller,
      description:
        "Professional decorating services for commercial environments, ensuring a clean, modern finish that represents your brand and withstands daily use.",
      details: [
        "Interior decorating",
        "Exterior decorating",
        "Brand representation",
        "Durable finishes",
        "Modern aesthetic",
        "Commercial-grade quality",
      ],
      features: [
        { icon: PaintRoller, text: "Professional finish" },
        { icon: Building2, text: "Brand representation" },
        { icon: ClipboardCheck, text: "Durable quality" },
        { icon: Wrench, text: "Commercial-grade" },
      ],
    },
    {
      id: 5,
      title: "Flooring Installations",
      icon: Square,
      description:
        "From hard-wearing vinyl to acoustic carpet tiles—our flooring solutions combine style, durability and ease of maintenance.",
      details: [
        "Hard-wearing vinyl",
        "Acoustic carpet tiles",
        "Specialist finishes",
        "Style and durability",
        "Easy maintenance",
        "Commercial-grade solutions",
      ],
      features: [
        { icon: Square, text: "Multiple options" },
        { icon: Wind, text: "Acoustic solutions" },
        { icon: ClipboardCheck, text: "Durable materials" },
        { icon: Building2, text: "Easy maintenance" },
      ],
    },
    {
      id: 6,
      title: "Demolition, Stripping Out and Removal Services",
      icon: Trash2,
      description:
        "Out with the old and in with the new - we will handle everything, from the first binbag out, to the last nail in.",
      details: [
        "Complete demolition services",
        "Stripping out old fixtures",
        "Removal and disposal",
        "Site clearance",
        "Waste management",
        "Full project preparation",
      ],
      features: [
        { icon: Trash2, text: "Complete removal" },
        { icon: Building2, text: "Site clearance" },
        { icon: ClipboardCheck, text: "Waste management" },
        { icon: Wrench, text: "Project preparation" },
      ],
    },
    {
      id: 7,
      title: "Air Conditioning & Ventilation",
      icon: Wind,
      description:
        "Full design and installation of efficient cooling, heating and ventilation systems to keep your workspace comfortable and compliant.",
      details: [
        "Full design services",
        "Installation expertise",
        "Cooling systems",
        "Heating solutions",
        "Ventilation systems",
        "Comfort and compliance",
      ],
      features: [
        { icon: Wind, text: "Full design & install" },
        { icon: Building2, text: "Comfort solutions" },
        { icon: ClipboardCheck, text: "Compliance guaranteed" },
        { icon: Wrench, text: "Efficient systems" },
      ],
    },
    {
      id: 8,
      title: "Electrical & Data",
      icon: Zap,
      description:
        "All aspects of electrical and data installation, maintenance and upgrades, including power distribution, lighting systems, structured cabling, data points and comms rooms.",
      details: [
        "Power distribution",
        "Lighting systems",
        "Structured cabling",
        "Data points and comms rooms",
        "Installation, maintenance and upgrades",
      ],
      features: [
        { icon: Zap, text: "Full electrical services" },
        { icon: Building2, text: "Data solutions" },
        { icon: ClipboardCheck, text: "Maintenance & upgrades" },
        { icon: Wrench, text: "Professional installation" },
      ],
    },
    {
      id: 9,
      title: "Space Planning",
      icon: Layout,
      description:
        "Optimise your office or warehouse layout to improve efficiency, flow and safety. We ensure your space works as hard as you do.",
      details: [
        "Office layout optimisation",
        "Warehouse space planning",
        "Improved efficiency",
        "Better flow",
        "Enhanced safety",
        "Maximised space utilisation",
      ],
      features: [
        { icon: Layout, text: "Layout optimisation" },
        { icon: Building2, text: "Efficiency focus" },
        { icon: ClipboardCheck, text: "Safety compliance" },
        { icon: Wrench, text: "Space maximisation" },
      ],
    },
    {
      id: 10,
      title: "Facilities Management",
      icon: Wrench,
      description:
        "Planned and reactive maintenance to keep your workspace operating smoothly. From small repairs to emergency call-outs, we're here when you need us.",
      details: [
        "Planned maintenance",
        "Reactive maintenance",
        "Small repairs",
        "Emergency call-outs",
        "Ongoing support",
        "Workspace operations",
      ],
      features: [
        { icon: Wrench, text: "Planned maintenance" },
        { icon: Building2, text: "Reactive support" },
        { icon: ClipboardCheck, text: "Emergency response" },
        { icon: Layout, text: "Ongoing operations" },
      ],
    },
    {
      id: 11,
      title: "Project & Site Management",
      icon: ClipboardCheck,
      description:
        "Clear communication, tight scheduling and meticulous supervision from start to finish. We manage contractors, budgets and timelines so you don't have to.",
      details: [
        "Clear communication",
        "Tight scheduling",
        "Meticulous supervision",
        "Contractor management",
        "Budget control",
        "Timeline management",
      ],
      features: [
        { icon: ClipboardCheck, text: "Full project management" },
        { icon: Building2, text: "Budget control" },
        { icon: Wrench, text: "Timeline management" },
        { icon: Layout, text: "Clear communication" },
      ],
    },
    {
      id: 12,
      title: "Office Furniture and Storage Solutions",
      icon: Sofa,
      description:
        "Comfortable, ergonomic modern office furniture is a must have for any working environment. Keep your employees comfortable and protected with our selection of regulation compliant office furniture and storage units.",
      details: [
        "Ergonomic office furniture",
        "Modern workspace solutions",
        "Storage units",
        "Regulation compliant",
        "Employee comfort and protection",
        "Complete furniture solutions",
      ],
      features: [
        { icon: Sofa, text: "Ergonomic furniture" },
        { icon: Building2, text: "Storage solutions" },
        { icon: ClipboardCheck, text: "Regulation compliant" },
        { icon: Wrench, text: "Complete solutions" },
      ],
    },
  ];

  // Function to get images for each service
  const getServiceImages = (serviceId: number): string[] => {
    const imageMap: { [key: number]: string[] } = {
      0: [FCFO3, FCFO4, OfficeFurniture1, FCFO3], // Full Commercial Fit-Out
      1: [
        officePartitioning1,
        officePartitioning2,
        officePartitioning3,
        officePartitioning4,
      ], // Office Partitioning
      2: [plastering1, plastering3, plastering4, plastering1], // Dry-Lining & Plastering
      3: [ceilings1, ceilings2, ceilings3, ceilings4], // Ceilings
      4: [decorating1, decorating2, decorating3, decorating4], // Interior & Exterior Decorating
      5: [flooring1, flooring2, flooring3, flooring4], // Flooring Installations
      6: [demolition1, demolition2, demolition3, demolition4], // Demolition, Stripping Out and Removal Services
      7: [
        airconVentilation,
        airconVentilation2,
        airconVentilation3,
        airconVentilation4,
      ], // Air Conditioning & Ventilation
      8: [elecData1, elecData2, elecData3, elecData4], // Electrical & Data
      9: [spacePlanning1, spacePlanning2, spacePlanning3, spacePlanning4], // Space Planning
      10: [facilitiesMan1, facilitiesMan2, facilitiesMan3, facilitiesMan4], // Facilities Management
      11: [projMan1, projMan2, projMan3, projMan4], // Project & Site Management
      12: [
        OfficeFurniture1,
        OfficeFurniture2,
        OfficeFurniture3,
        OfficeFurniture4,
      ], // Office Furniture and Storage Solutions
    };
    return imageMap[serviceId] || [FCFO3, FCFO4, OfficeFurniture1, FCFO3];
  };

  const activeService = services[activeTab];
  const serviceImages = getServiceImages(activeTab);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Full commercial and office interiors fit-out services including
              partitioning, ceiling systems, decorating, flooring, electrical
              and maintenance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-12">
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm ${
                  activeTab === index
                    ? "bg-primary-600 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                <service.icon className="w-4 h-4" />
                <span>{service.title}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              {activeTab === 9 ? (
                // Space Planning - Single column with images stacked below
                <div>
                  {/* Content */}
                  <div className="p-8 lg:p-12">
                    <div className="flex items-start space-x-4 mb-8">
                      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <activeService.icon className="w-8 h-8 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-3xl font-bold text-gray-900 mb-3">
                          {activeService.title}
                        </h2>
                        <p className="text-gray-600">
                          {activeService.description}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-900">
                        What's Included:
                      </h3>
                      <ul className="space-y-3">
                        {activeService.details.map((detail, index) => (
                          <li
                            key={index}
                            className="flex items-start space-x-3"
                          >
                            <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-4 mt-8">
                      {activeService.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <feature.icon className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm font-medium text-gray-700">
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-8 pt-6 border-t border-gray-200 mb-8">
                      <div className="flex justify-center">
                        <Link
                          to="/contact#contact-form"
                          onClick={() => {
                            sessionStorage.setItem(
                              "scrollToContactForm",
                              "true"
                            );
                          }}
                          className="btn-primary inline-flex items-center space-x-2"
                        >
                          <span>Contact Us</span>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Images stacked vertically at half size with blue background */}
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 px-8 lg:px-12 py-8 lg:py-12 space-y-6">
                    {serviceImages.map((image, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg overflow-hidden shadow-lg flex justify-center"
                      >
                        <img
                          src={image}
                          alt={`${activeService.title} project ${index + 1}`}
                          className="w-1/2 h-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Default 2-column layout for all other tabs
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Left Side - Content */}
                  <div className="p-8 lg:p-12">
                    <div className="flex items-start space-x-4 mb-8">
                      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <activeService.icon className="w-8 h-8 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-3xl font-bold text-gray-900 mb-3">
                          {activeService.title}
                        </h2>
                        <p className="text-gray-600">
                          {activeService.description}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-900">
                        What's Included:
                      </h3>
                      <ul className="space-y-3">
                        {activeService.details.map((detail, index) => (
                          <li
                            key={index}
                            className="flex items-start space-x-3"
                          >
                            <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-4 mt-8">
                      {activeService.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <feature.icon className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm font-medium text-gray-700">
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="flex justify-center">
                        <Link
                          to="/contact#contact-form"
                          onClick={() => {
                            sessionStorage.setItem(
                              "scrollToContactForm",
                              "true"
                            );
                          }}
                          className="btn-primary inline-flex items-center space-x-2"
                        >
                          <span>Contact Us</span>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Visual */}
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-8 lg:p-12">
                    <div className="grid grid-cols-2 gap-4 h-full">
                      <div className="space-y-4">
                        <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-sm">
                          <img
                            src={serviceImages[0]}
                            alt={`${activeService.title} project 1`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-sm">
                          <img
                            src={serviceImages[1]}
                            alt={`${activeService.title} project 2`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="space-y-4 pt-8">
                        <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-sm">
                          <img
                            src={serviceImages[2]}
                            alt={`${activeService.title} project 3`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="aspect-square bg-white rounded-lg overflow-hidden shadow-sm">
                          <img
                            src={serviceImages[3]}
                            alt={`${activeService.title} project 4`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to improve your workspace?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about transforming your office,
              warehouse or facility.
            </p>
            <div className="flex justify-center">
              <Link
                to="/contact#contact-form"
                onClick={() => {
                  sessionStorage.setItem("scrollToContactForm", "true");
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
